import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from '../components/ImageWithFallback';
import PlayerCarousel from '../components/PlayerCarousel';
import AcademyPlayerCarousel from '../components/AcademyPlayerCarousel';
import AcademyImagesCarousel from '../components/AcademyImagesCarousel';
import SponsorsCarousel from '../components/SponsorsCarousel';
import ShareButton from '../components/ShareButton';
import NewsModal from '../components/NewsModal';
import EditableContent from '../components/EditableContent';
import { useNews, useTeamMembers } from '../hooks/useSupabase';
import { useSite } from '../contexts/SiteContext';
import supabase from '../lib/supabase';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiTrendingUp, FiUsers, FiArrowRight, FiUserPlus } = FiIcons;

const HomePage = () => {
  const { data: recentNews, loading: newsLoading } = useNews(4);
  const { data: academyPlayers } = useTeamMembers(false);
  const { siteConfig } = useSite();
  const [academyCategory, setAcademyCategory] = useState('K17');
  const [selectedNews, setSelectedNews] = useState(null);
  const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);

  const academyCategories = useMemo(() => {
    const categories = new Set();
    (academyPlayers || []).forEach((player) => {
      (player.team_categories || []).forEach((category) => {
        if (typeof category === 'string' && /^K\d+$/i.test(category.trim())) {
          categories.add(category.trim().toUpperCase());
        }
      });
    });

    return Array.from(categories).sort((a, b) => {
      const aNumber = Number(a.replace(/\D/g, '')) || 0;
      const bNumber = Number(b.replace(/\D/g, '')) || 0;
      return bNumber - aNumber;
    });
  }, [academyPlayers]);

  useEffect(() => {
    if (academyCategories.length > 0 && !academyCategories.includes(academyCategory)) {
      setAcademyCategory(academyCategories[0]);
    }
  }, [academyCategories, academyCategory]);

  useEffect(() => {
    const newsId = new URLSearchParams(window.location.search).get('news');
    if (!newsId) return;

    const loadNews = async () => {
      const { data, error } = await supabase
        .from('news_aox1234')
        .select('*')
        .eq('id', newsId)
        .single();

      if (!error && data) {
        setSelectedNews(data);
        setIsNewsModalOpen(true);
      }
    };

    loadNews();
  }, []);

  const closeNewsModal = () => {
    setIsNewsModalOpen(false);
    setTimeout(() => setSelectedNews(null), 250);
    if (window.location.search.includes('news=')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  const quickLinks = [
    { key: 'matches', icon: FiCalendar, href: '/matches', fallback: 'Πρόγραμμα Αγώνων' },
    { key: 'standings', icon: FiTrendingUp, href: '/matches', fallback: 'Βαθμολογία' },
    { key: 'team', icon: FiUsers, href: '/team', fallback: 'Η Ομάδα' },
  ];

  return (
    <div>
      <section className="relative min-h-[88vh] flex items-center justify-center overflow-hidden bg-primary-800 text-white">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1574629810360-7efbbe195018?ixlib=rb-4.0.3')] bg-cover bg-center scale-105" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-800/80 to-blue-700/75" />
        </div>

        <motion.div initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative z-10 px-4 w-full">
          <div className="max-w-3xl mx-auto bg-black/15 backdrop-blur-sm rounded-3xl border border-white/10 px-6 py-9 md:px-10 md:py-11 shadow-2xl text-center">
            <ImageWithFallback
              src={siteConfig.logo_url}
              alt={siteConfig.site_name}
              className="w-24 h-24 mx-auto rounded-full object-contain bg-white/10 mb-4 border border-white/30 shadow-lg"
              fallback={<div className="w-24 h-24 mx-auto rounded-full bg-white/10 flex items-center justify-center text-3xl font-black">ΑΟΧ</div>}
            />

            <h1 className="text-4xl md:text-6xl font-extrabold mb-3 drop-shadow-md">
              <EditableContent page="home" section="hero" contentKey="title" defaultValue={siteConfig.site_name || 'Α.Ο. Χανιά'} tag="span" />
            </h1>
            <p className="text-lg md:text-2xl mb-2 text-blue-100">
              <EditableContent page="home" section="hero" contentKey="subtitle" defaultValue={siteConfig.site_description || 'Γυναικεία Ομάδα Ποδοσφαίρου'} tag="span" />
            </p>
            <p className="text-sm md:text-lg mb-7 text-primary-200 uppercase tracking-[0.15em]">
              <EditableContent page="home" section="hero" contentKey="category" defaultValue="Γ' Κατηγορία Γυναικών • 8ος Όμιλος • 2026-27" tag="span" />
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/team" className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-semibold flex items-center justify-center shadow-lg">
                <EditableContent page="home" section="hero" contentKey="primary_button" defaultValue="Γνώρισε την Ομάδα" tag="span" />
                <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/matches" className="border-2 border-white/70 hover:bg-white hover:text-primary-700 px-8 py-3 rounded-lg font-semibold transition-all flex items-center justify-center">
                <EditableContent page="home" section="hero" contentKey="secondary_button" defaultValue="Πρόγραμμα Αγώνων" tag="span" />
              </Link>
            </div>
          </div>
        </motion.div>

        <div className="absolute top-4 right-4 z-20">
          <ShareButton title={`${siteConfig.site_name || 'Α.Ο. Χανιά'} - Αρχική`} description={siteConfig.site_description} imageUrl={siteConfig.logo_url || ''} compact variant="outline" className="bg-white/20 border-white/30 text-white" />
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AcademyImagesCarousel />
          <h2 className="text-3xl md:text-4xl font-bold mt-8 mb-4">
            <EditableContent page="home" section="academy_cta" contentKey="title" defaultValue="Καλώς ήρθατε στην Ακαδημία!" tag="span" />
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            <EditableContent page="home" section="academy_cta" contentKey="description" defaultValue="Εγγραφές ανοικτές για κορίτσια 5-16 ετών" tag="span" />
          </p>
          <Link to="/academy-registration" className="inline-flex items-center bg-yellow-400 hover:bg-yellow-500 text-blue-900 px-10 py-4 rounded-xl font-bold text-lg shadow-xl">
            <SafeIcon icon={FiUserPlus} className="w-6 h-6 mr-3" />
            Κάνε Εγγραφή στην Ακαδημία
          </Link>
          <div className="mt-6 text-blue-200 text-sm">
            <EditableContent page="home" section="academy_cta" contentKey="details" defaultValue="Ηλικίες 5-16 ετών • Έμπειρο προπονητικό επιτελείο" tag="p" />
          </div>
        </div>
      </section>

      <SponsorsCarousel />
      <PlayerCarousel />

      <section className="py-16 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              <EditableContent page="home" section="academy_overview" contentKey="title" defaultValue="Ακαδημία Ποδοσφαίρου" tag="span" />
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              <EditableContent page="home" section="academy_overview" contentKey="subtitle" defaultValue="Το μέλλον του ΑΟΧ" tag="span" />
            </p>

            {academyCategories.length > 0 && (
              <div className="flex justify-center gap-3 mb-8 flex-wrap">
                {academyCategories.map((category) => (
                  <button key={category} onClick={() => setAcademyCategory(category)} className={`px-6 py-2 rounded-full border text-sm font-medium ${academyCategory === category ? 'bg-primary-600 text-white border-primary-600 shadow-md' : 'bg-white text-gray-600 border-gray-300'}`}>
                    {category}
                  </button>
                ))}
              </div>
            )}
          </div>

          {academyCategories.length > 0 && <AcademyPlayerCarousel category={academyCategory} title={`Ομάδα ${academyCategory}`} isVisible />}

          <div className="text-center mt-8">
            <Link to="/academy" className="text-primary-600 font-medium hover:text-primary-800">
              <EditableContent page="home" section="academy_overview" contentKey="more_link" defaultValue="Μάθετε περισσότερα για την Ακαδημία →" tag="span" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          {quickLinks.map((link) => (
            <Link key={link.key} to={link.href} className="block p-6 bg-gray-50 hover:bg-primary-50 rounded-xl transition-all group">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center"><SafeIcon icon={link.icon} className="w-6 h-6 text-primary-600" /></div>
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-700">
                  <EditableContent page="home" section="quick_links" contentKey={link.key} defaultValue={link.fallback} tag="span" />
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"><EditableContent page="home" section="news" contentKey="title" defaultValue="Τελευταία Νέα" tag="span" /></h2>
            <p className="text-xl text-gray-600"><EditableContent page="home" section="news" contentKey="subtitle" defaultValue="Μείνετε ενημερωμένοι για τις εξελίξεις της ομάδας μας" tag="span" /></p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {newsLoading ? (
              <div className="col-span-full text-center py-12">Φόρτωση...</div>
            ) : (
              (recentNews || []).map((news) => (
                <article key={news.id} onClick={() => { setSelectedNews(news); setIsNewsModalOpen(true); }} className="bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-100 cursor-pointer overflow-hidden group">
                  <div className="aspect-video bg-gray-100 overflow-hidden relative">
                    <ImageWithFallback src={news.image || news.image_url} alt={news.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold bg-primary-600/90 text-white">{news.category || 'Νέα'}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{news.title}</h3>
                    <p className="text-gray-600 text-sm line-clamp-3">{news.excerpt || (news.content ? `${news.content.substring(0, 150)}...` : '')}</p>
                    <div className="mt-5 pt-4 border-t flex items-center justify-between text-sm">
                      <span className="text-primary-600 font-semibold">Διαβάστε περισσότερα</span>
                      <span className="text-gray-500">{news.published_at ? new Date(news.published_at).toLocaleDateString('el-GR') : ''}</span>
                    </div>
                  </div>
                </article>
              ))
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/news" className="inline-flex items-center px-8 py-3 rounded-xl font-semibold border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white">
              <EditableContent page="home" section="news" contentKey="button" defaultValue="Όλα τα Νέα" tag="span" />
              <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-primary-700" style={{ backgroundColor: siteConfig.primary_color }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
          <div><div className="text-4xl font-bold mb-2">{siteConfig.stats_founding_year || '1945'}</div><div className="text-lg"><EditableContent page="home" section="stats" contentKey="founded_label" defaultValue="Έτος Ίδρυσης" tag="span" /></div></div>
          <div><div className="text-4xl font-bold mb-2">{siteConfig.stats_players_count || '23'}</div><div className="text-lg"><EditableContent page="home" section="stats" contentKey="players_label" defaultValue="Παίκτριες" tag="span" /></div></div>
          <div><div className="text-4xl font-bold mb-2">{siteConfig.stats_league_division || "Γ'"}</div><div className="text-lg"><EditableContent page="home" section="stats" contentKey="division_label" defaultValue="Εθνική Κατηγορία" tag="span" /></div></div>
          <div><div className="text-4xl font-bold mb-2">{siteConfig.stats_years_history || '81'}</div><div className="text-lg"><EditableContent page="home" section="stats" contentKey="history_label" defaultValue="Χρόνια Ιστορίας" tag="span" /></div></div>
        </div>
      </section>

      <NewsModal article={selectedNews} isOpen={isNewsModalOpen} onClose={closeNewsModal} />
    </div>
  );
};

export default HomePage;
